import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithSessionHandling } from '../utils/authFetch';
import { parseApiDateTime } from '../utils/apiDateTime';

type FunnelFilter = '' | 'trial_signup' | 'standard_signup' | 'login';

interface FunnelSummaryItem {
  funnel?: string | null;
  step?: string | null;
  eventName: string;
  eventCount: number;
  uniqueVisitors: number;
  uniqueIps: number;
  uniqueEmails: number;
  uniqueUsers: number;
  conversionRate?: number | null;
}

interface IpSummaryItem {
  ipAddress: string;
  firstSeenAt: string;
  lastSeenAt: string;
  eventCount: number;
  uniqueVisitors: number;
  trialRequests: number;
  registrations: number;
  subscriptionIntents: number;
  subscriptionSuccesses: number;
  primaryFunnel?: string | null;
}

interface AnalyticsEventItem {
  id: number;
  eventName: string;
  funnel?: string | null;
  step?: string | null;
  status?: string | null;
  visitorId?: string | null;
  ipAddress?: string | null;
  userId?: string | null;
  email?: string | null;
  username?: string | null;
  occurredAt: string;
  path?: string | null;
}

interface DeleteAnalyticsEventsResponse {
  deletedCount: number;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const JST_TIME_ZONE = 'Asia/Tokyo';

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-';
  }
  const date = parseApiDateTime(value);
  if (!date) {
    return value;
  }
  return new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
};

const formatFunnel = (value?: string | null) => {
  if (value === 'trial_signup') return 'お試し';
  if (value === 'standard_signup') return '通常登録';
  if (value === 'login') return 'ログイン';
  return value || '-';
};

const AnalyticsLogView: React.FC = () => {
  const { token, logout } = useAuth();
  const [funnel, setFunnel] = useState<FunnelFilter>('');
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [startDateInput, setStartDateInput] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [endDate, setEndDate] = useState('');
  const [funnelItems, setFunnelItems] = useState<FunnelSummaryItem[]>([]);
  const [ipItems, setIpItems] = useState<IpSummaryItem[]>([]);
  const [eventItems, setEventItems] = useState<AnalyticsEventItem[]>([]);
  const [eventTotal, setEventTotal] = useState(0);
  const [eventPage, setEventPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [loading, setLoading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const buildBaseParams = () => {
    const params = new URLSearchParams();
    if (funnel) params.set('funnel', funnel);
    if (query) params.set('query', query);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return params;
  };

  const fetchAnalytics = async (pageOverride?: number, showError = true) => {
    setLoading(true);
    const authToken = token;
    try {
      const summaryParams = buildBaseParams();
      summaryParams.delete('query');
      const ipParams = buildBaseParams();
      ipParams.set('limit', '100');
      ipParams.set('page', '1');
      const eventParams = buildBaseParams();
      eventParams.set('limit', String(rowsPerPage));
      eventParams.set('page', String((pageOverride ?? eventPage) + 1));

      const [summaryResult, ipResult, eventsResult] = await Promise.all([
        fetchWithSessionHandling(
          `${API_BASE_URL}/api/admin/db/analytics/funnel-summary?${summaryParams.toString()}`,
          { headers: { Authorization: `Bearer ${authToken}` } },
          { logout },
        ),
        fetchWithSessionHandling(
          `${API_BASE_URL}/api/admin/db/analytics/ip-summary?${ipParams.toString()}`,
          { headers: { Authorization: `Bearer ${authToken}` } },
          { logout },
        ),
        fetchWithSessionHandling(
          `${API_BASE_URL}/api/admin/db/analytics/events?${eventParams.toString()}`,
          { headers: { Authorization: `Bearer ${authToken}` } },
          { logout },
        ),
      ]);

      if (summaryResult.sessionExpired || ipResult.sessionExpired || eventsResult.sessionExpired) {
        return false;
      }
      if (!summaryResult.response.ok || !ipResult.response.ok || !eventsResult.response.ok) {
        if (showError) {
          setSnackbar({ open: true, message: '導線ログの読み込みに失敗しました', severity: 'error' });
        }
        return false;
      }

      const [summaryData, ipData, eventsData] = await Promise.all([
        summaryResult.response.json(),
        ipResult.response.json(),
        eventsResult.response.json(),
      ]);
      setFunnelItems(summaryData.items ?? []);
      setIpItems(ipData.items ?? []);
      setEventItems(eventsData.items ?? []);
      setEventTotal(eventsData.total ?? 0);
      return true;
    } catch (error) {
      console.error('導線ログの取得に失敗しました', error);
      if (showError) {
        setSnackbar({ open: true, message: '導線ログの読み込みに失敗しました', severity: 'error' });
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnel, query, startDate, endDate, eventPage, rowsPerPage]);

  const handleSearch = () => {
    if (startDateInput && endDateInput && startDateInput > endDateInput) {
      setSnackbar({ open: true, message: '開始日は終了日以前の日付を指定してください', severity: 'error' });
      return;
    }
    setEventPage(0);
    setQuery(queryInput.trim());
    setStartDate(startDateInput);
    setEndDate(endDateInput);
  };

  const handleClear = () => {
    setFunnel('');
    setQueryInput('');
    setQuery('');
    setStartDateInput('');
    setStartDate('');
    setEndDateInput('');
    setEndDate('');
    setEventPage(0);
  };

  const handleFunnelChange = (event: SelectChangeEvent<FunnelFilter>) => {
    setFunnel(event.target.value as FunnelFilter);
    setEventPage(0);
  };

  const handleOpenDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (!isDeleting) {
      setIsDeleteDialogOpen(false);
    }
  };

  const handleDeleteAll = async () => {
    const authToken = token;

    setIsDeleting(true);
    try {
      const { response, sessionExpired } = await fetchWithSessionHandling(
        `${API_BASE_URL}/api/admin/db/analytics/events`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${authToken}` },
        },
        { logout },
      );
      if (sessionExpired) {
        return;
      }
      if (!response.ok) {
        setSnackbar({ open: true, message: '導線ログの削除に失敗しました', severity: 'error' });
        return;
      }

      const data: DeleteAnalyticsEventsResponse = await response.json();
      setIsDeleteDialogOpen(false);
      setEventPage(0);
      const refreshed = await fetchAnalytics(0, false);
      if (!refreshed) {
        setFunnelItems([]);
        setIpItems([]);
        setEventItems([]);
        setEventTotal(0);
      }
      setSnackbar({
        open: true,
        message: `導線ログを${data.deletedCount}件削除しました`,
        severity: 'success',
      });
    } catch (error) {
      console.error('導線ログの削除に失敗しました', error);
      setSnackbar({ open: true, message: '導線ログの削除に失敗しました', severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={2}
        sx={{ mb: 1 }}
      >
        <Typography variant="h4">
          導線ログ
        </Typography>
        <Button
          variant="contained"
          color="error"
          onClick={handleOpenDeleteDialog}
          disabled={loading || isDeleting}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, whiteSpace: 'nowrap' }}
        >
          ログ削除
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        未登録者を含む導線イベントを180日間保存します。人数は訪問者IDを主指標とし、IPは施設・回線単位の参考値です。
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} useFlexGap>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel id="analytics-funnel-label">ファネル</InputLabel>
          <Select labelId="analytics-funnel-label" value={funnel} label="ファネル" onChange={handleFunnelChange}>
            <MenuItem value="">すべて</MenuItem>
            <MenuItem value="trial_signup">お試し</MenuItem>
            <MenuItem value="standard_signup">通常登録</MenuItem>
            <MenuItem value="login">ログイン</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="検索"
          placeholder="メール・ユーザ名・IP・訪問者ID・パス"
          value={queryInput}
          onChange={(event) => setQueryInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleSearch();
          }}
          fullWidth
        />
        <TextField
          label="開始日"
          type="date"
          value={startDateInput}
          onChange={(event) => setStartDateInput(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
        <TextField
          label="終了日"
          type="date"
          value={endDateInput}
          onChange={(event) => setEndDateInput(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
        <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
          <Button variant="contained" onClick={handleSearch} sx={{ whiteSpace: 'nowrap' }}>
            絞り込み
          </Button>
          <Button variant="outlined" onClick={handleClear} sx={{ whiteSpace: 'nowrap' }}>
            クリア
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.65)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          ファネル集計
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ファネル</TableCell>
              <TableCell>イベント</TableCell>
              <TableCell align="right">件数</TableCell>
              <TableCell align="right">訪問者</TableCell>
              <TableCell align="right">IP</TableCell>
              <TableCell align="right">メール</TableCell>
              <TableCell align="right">到達率</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {funnelItems.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">集計対象のイベントはありません</TableCell></TableRow>
            ) : funnelItems.map((item) => (
              <TableRow key={`${item.funnel}-${item.step}-${item.eventName}`}>
                <TableCell>{formatFunnel(item.funnel)}</TableCell>
                <TableCell>{item.eventName}</TableCell>
                <TableCell align="right">{item.eventCount}</TableCell>
                <TableCell align="right">{item.uniqueVisitors}</TableCell>
                <TableCell align="right">{item.uniqueIps}</TableCell>
                <TableCell align="right">{item.uniqueEmails}</TableCell>
                <TableCell align="right">{item.conversionRate == null ? '-' : `${item.conversionRate}%`}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          IP別集計
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>IP</TableCell>
              <TableCell>主な導線</TableCell>
              <TableCell align="right">件数</TableCell>
              <TableCell align="right">訪問者</TableCell>
              <TableCell align="right">お試し申込</TableCell>
              <TableCell align="right">通常登録</TableCell>
              <TableCell align="right">契約作成</TableCell>
              <TableCell align="right">支払成功</TableCell>
              <TableCell>最終アクセス</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ipItems.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center">該当するIP集計はありません</TableCell></TableRow>
            ) : ipItems.map((item) => (
              <TableRow key={item.ipAddress}>
                <TableCell>{item.ipAddress}</TableCell>
                <TableCell>{formatFunnel(item.primaryFunnel)}</TableCell>
                <TableCell align="right">{item.eventCount}</TableCell>
                <TableCell align="right">{item.uniqueVisitors}</TableCell>
                <TableCell align="right">{item.trialRequests}</TableCell>
                <TableCell align="right">{item.registrations}</TableCell>
                <TableCell align="right">{item.subscriptionIntents}</TableCell>
                <TableCell align="right">{item.subscriptionSuccesses}</TableCell>
                <TableCell>{formatDateTime(item.lastSeenAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          イベント明細
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>日時</TableCell>
              <TableCell>イベント</TableCell>
              <TableCell>
                <Box component="span" sx={{ display: 'inline-block', minWidth: '4em', whiteSpace: 'nowrap' }}>
                  ファネル
                </Box>
              </TableCell>
              <TableCell>状態</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>メール</TableCell>
              <TableCell>パス</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eventItems.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">該当するイベントはありません</TableCell></TableRow>
            ) : eventItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{formatDateTime(item.occurredAt)}</TableCell>
                <TableCell>{item.eventName}</TableCell>
                <TableCell>
                  <Box component="span" sx={{ display: 'inline-block', minWidth: '4em', whiteSpace: 'nowrap' }}>
                    {formatFunnel(item.funnel)}
                  </Box>
                </TableCell>
                <TableCell>{item.status || '-'}</TableCell>
                <TableCell>{item.ipAddress || '-'}</TableCell>
                <TableCell>{item.email || '-'}</TableCell>
                <TableCell>{item.path || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={eventTotal}
          page={eventPage}
          onPageChange={(_, nextPage) => setEventPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setEventPage(0);
          }}
          rowsPerPageOptions={[100, 500]}
          labelRowsPerPage="表示件数"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Box>

      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>導線ログ削除の確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            保存されている導線ログをすべて削除します。この操作は元に戻せません。よろしいですか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
            キャンセル
          </Button>
          <Button onClick={handleDeleteAll} color="error" variant="contained" disabled={isDeleting}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AnalyticsLogView;
